import React, { useState } from "react";
import jsPDF from "jspdf";
import LoadingSpinner from "./LoadingSpinner";
import classes from "./GeneratePDF.module.css";
import { getWhiteLabelTheme } from "../../whiteLabel/themes";
import { ReportLang, isGeorgianReportLang } from "../../i18n/reportLang";

interface GeneratePDFProps {
  userName: string;
  type: string;
  setIsCreatingPDF: (isCreating: boolean) => void;
  /** White-label reports name their file after the brand, not the PNOE type. */
  customType?: string;
  /** Language of the rendered report; "zh" needs CJK fonts registered first. */
  lang?: ReportLang;
  /**
   * The raw `?lang=` the backend localized the report text in. Separate from
   * `lang` because Georgian ("ge") is backend-only and not a `ReportLang`, yet
   * still needs Georgian glyphs registered before export.
   */
  backendLang?: string | null;
}

const GeneratePDF: React.FC<GeneratePDFProps> = ({
  userName,
  type,
  setIsCreatingPDF,
  customType,
  lang,
  backendLang,
}) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const name = userName.replace(/ /g, "_");

  let typeName: string;
  const whiteLabelFileName = getWhiteLabelTheme(customType)?.pdfFileName;
  if (whiteLabelFileName) {
    typeName = whiteLabelFileName;
  } else if (type === "10") {
    typeName = "Longevity_AMR";
  } else if (type === "11") {
    typeName = "Longevity_RMR";
  } else if (type === "12") {
    typeName = "Weight_Loss_AMR";
  } else if (type === "13") {
    typeName = "Weight_Loss_RMR";
  } else if (type === "14") {
    typeName = "Performance_AMR";
  } else if (type === "15") {
    typeName = "Performance_RMR";
  } else if (type === "17") {
    typeName = "Frank_Shallenberger_RMR";
  } else if (type === "18") {
    typeName = "Frank_Shallenberger_ΑMR";
  } else if (type === "19") {
    typeName = "Affiliate_Cheatsheet";
  } else if (type === "services") {
    typeName = "Service";
  } else {
    typeName = "Report";
  }

  // jsPDF's html() plugin brackets the render with context2d.save(true) /
  // restore(true). Both loop over every page that exists *at call time* and
  // write one operator to each: `q` while only page 1 exists, then `Q` once
  // html2canvas has auto-paged the report. Every page after the first therefore
  // ends with a `Q` that has no matching `q`, and Acrobat reports "An error
  // exists on this page" (poppler: "Restoring state when no valid states to
  // pop"). Upstream jsPDF master still does this. Each page's content is an
  // array of lines with `q`/`Q` as standalone entries, so drop any `Q` that
  // would take the nesting depth below zero. The reverse also happens: an
  // html2canvas save() with no restore() leaves a `q` open at the end of a
  // page (seen on page 1 of a 65-page export), which viewers tolerate but the
  // spec forbids — close those with `Q`. Nothing visible changes either way.
  const balanceGraphicsState = (pdf: jsPDF) => {
    const pages = (pdf.internal as unknown as { pages: string[][] }).pages;
    for (let i = 1; i < pages.length; i++) {
      if (!pages[i]) continue;
      let depth = 0;
      pages[i] = pages[i].filter((line) => {
        if (line === "q") {
          depth++;
          return true;
        }
        if (line === "Q") {
          if (depth === 0) return false;
          depth--;
        }
        return true;
      });
      for (; depth > 0; depth--) pages[i].push("Q");
    }
  };

  const generatePDF = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    // Only Chinese needs this, and only before doc.html() reads the font table:
    // the bundled fonts have no Han glyphs, so without it every Chinese
    // character is dropped from the export. Loaded on demand — three 160 KB
    // subsets that no other language pays for.
    if (lang === "zh") {
      const { registerCjkPdfFonts } = await import(
        "../../whiteLabel/cjkPdfFont"
      );
      await registerCjkPdfFonts(doc);
    }

    // Georgian has the same problem — no bundled font has a single Georgian
    // glyph — but the fix is different: the standard report pages hard-code
    // the AktivGroteskEx family names, so the Georgian subsets are registered
    // under those names and take over the font table. Loaded on demand — five
    // 45 KB files that no other language pays for.
    if (isGeorgianReportLang(backendLang)) {
      const { registerGeorgianPdfFonts } = await import(
        "../../pdfFonts/georgianPdfFont"
      );
      await registerGeorgianPdfFonts(doc);
    }

    // html2canvas measures the collapsed space at every soft line wrap as an
    // empty box (its range yields two zero-width client rects) and still asks
    // the canvas to draw that lone space at (0, 0). jsPDF's context2d puts the
    // glyph 100000pt off-page, which is harmless on the legacy pages, but
    // inside the CSS-scaled subtree (ScaledNewReportPages) it lands on the
    // current page and first re-emits the active clip rectangle for the wrong
    // page. Clips only ever intersect, so every glyph drawn after it on that
    // page — the rest of the paragraph — is clipped away until the next
    // restore. A space has no ink, so skipping whitespace-only draws is
    // lossless and removes the stray clip with it.
    const originalFillText = doc.context2d.fillText.bind(doc.context2d);
    doc.context2d.fillText = (text, x, y, maxWidth) => {
      if (text.trim() === "") return;
      originalFillText(text, x, y, maxWidth);
    };

    // html2canvas draws every <img> inside ctx.save()/restore() and swallows
    // any throw with "Error loading image" — but the throw skips the restore,
    // so its save/restore stack is off by one for the rest of the render and
    // every restore from then on reinstates the wrong state. Under the
    // transform: scale(1.3314) wrapper that means the scale is re-applied
    // cumulatively on each element (1.3314^n), the layout grows geometrically
    // and a 34-page report comes out at ~1,800 pages. jsPDF throws exactly
    // that way for a tainted image: its html() defaults to allowTaint: true,
    // so a cross-origin image whose host sends no CORS header for the current
    // origin (mypnoe.blob.core.windows.net allows welcome*.mypnoe.com but not
    // localhost) loads tainted, and reading its pixels is a SecurityError.
    // Skipping the image keeps the stack balanced; the rest of the report
    // exports correctly without it.
    const context2d = doc.context2d;
    const originalDrawImage = context2d.drawImage.bind(context2d) as (
      ...args: unknown[]
    ) => void;
    context2d.drawImage = ((...args: unknown[]) => {
      try {
        originalDrawImage(...args);
      } catch (e) {
        console.warn("PDF export: skipping image that could not be drawn", e);
      }
    }) as typeof context2d.drawImage;

    const content = document.querySelector("#content") as HTMLElement;
    doc.html(content, {
      callback: function (pdf: jsPDF) {
        // @ts-ignore
        const pageCount = doc.internal.getNumberOfPages();
        if (type !== "services") {
          pdf.deletePage(pageCount);
        }
        pdf.setFont("AktivGroteskEx_A_Bd", "normal");
        pdf.setFont("AktivGroteskEx_A_Lt", "normal");
        pdf.setFont("AktivGroteskEx_A_Md", "normal");
        pdf.setFont("AktivGroteskEx_A_Rg", "normal");
        pdf.setFont("AktivGroteskEx_A_XBd", "normal");
        pdf.setFont("Poppins-Light", "normal");
        balanceGraphicsState(pdf);
        pdf.save(`${typeName}_Report_${name.replace(/\./g, "")}`);
        setIsGeneratingPDF(false);
      },
      html2canvas: { scale: 0.75 },
    });
  };

  const generatePDFHandler = () => {
    setIsGeneratingPDF(true);
    setIsCreatingPDF(true);
    setTimeout(() => {
      generatePDF().catch((e) => {
        console.error("PDF generation failed", e);
        setIsGeneratingPDF(false);
      });
      setIsCreatingPDF(false);
    }, 3000);
  };

  const pdfGeneratorLoader = <LoadingSpinner size="small" />;

  return (
    <div className={classes.btnContainer} onClick={generatePDFHandler}>
      <div className={classes.inner}>
        <div className={classes.text}>Generate PDF</div>
        {isGeneratingPDF ? (
          pdfGeneratorLoader
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path d="M7.126,14.692h5.748c0.39,0,0.706-0.316,0.706-0.706c0-0.39-0.315-0.706-0.706-0.706H7.126c-0.39,0-0.706,0.316-0.706,0.706C6.42,14.376,6.736,14.692,7.126,14.692z" />
            <path d="M7.126,16.899h4.641c0.39,0,0.706-0.315,0.706-0.706c0-0.389-0.316-0.706-0.706-0.706H7.126c-0.39,0-0.706,0.317-0.706,0.706C6.42,16.584,6.736,16.899,7.126,16.899z" />
            <path d="M18.933,3.163h-3.309V1.686c0-0.392-0.316-0.706-0.706-0.706H5.061c-0.39,0-0.706,0.314-0.706,0.706v1.477H1.067c-0.39,0-0.706,0.317-0.706,0.706v7.997c0,0.391,0.316,0.706,0.706,0.706h3.301v5.743c0,0.39,0.316,0.706,0.706,0.706h7.801c0.192,0,0.373-0.077,0.507-0.215l2.054-2.121c0.127-0.131,0.198-0.306,0.198-0.491v-3.622h3.3c0.39,0,0.706-0.315,0.706-0.706V3.869C19.638,3.481,19.323,3.163,18.933,3.163z M14.222,15.908l-1.647,1.701H5.779v-5.037h8.443V15.908z M18.227,11.16h-3.3H5.074H1.773V4.575h3.288c0.39,0,0.706-0.314,0.706-0.706V2.392h8.446v1.477c0,0.392,0.316,0.706,0.706,0.706h3.309V11.16z" />
          </svg>
        )}
      </div>
    </div>
  );
};

export default GeneratePDF;
